import type { ReactNode } from "react";
import { LockKeyhole } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { preserveAdminDevSubdomain } from "@/lib/adminOrigin";
import {
  PLAN_FEATURE_LABELS,
  resolvePlanFeatureAccess,
  type PlanFeature,
} from "@/lib/planEntitlements";

interface PlanFeatureGateProps {
  feature: PlanFeature;
  children: ReactNode;
  featureName?: string;
  className?: string;
  contentClassName?: string;
  variant?: "page" | "section" | "compact";
  showUpgradeAction?: boolean;
}

const PlanFeatureGate = ({
  feature,
  children,
  featureName = PLAN_FEATURE_LABELS[feature],
  className,
  contentClassName,
  variant = "section",
  showUpgradeAction = true,
}: PlanFeatureGateProps) => {
  const { subscriptionTier, isStaff } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const access = resolvePlanFeatureAccess(subscriptionTier, feature);

  if (access.allowed || !access.requiredPlanName) return <>{children}</>;

  const canManageBilling = !isStaff;
  const upgradeText = `Upgrade to ${access.requiredPlanName} plan to unlock ${featureName}`;

  const openBilling = () => {
    setSearchParams(
      (current) => preserveAdminDevSubdomain(
        current,
        new URLSearchParams({ tab: "settings", settings: "billing" }),
      ),
      { replace: true },
    );
  };

  // The preview and the upgrade card share one grid cell, so a short preview
  // grows to fit the card instead of clipping it. A gate given a fixed height
  // keeps that height (the 1fr row fills it, like an inset overlay would).
  return (
    <div
      className={cn(
        "relative isolate grid grid-cols-1 grid-rows-1 overflow-hidden",
        variant === "page" && "min-h-[calc(100vh-7rem)]",
        variant === "section" && "min-h-40 rounded-xl",
        variant === "compact" && "min-h-12",
        className,
      )}
      data-plan-feature={feature}
      data-required-plan={access.requiredPlanKey ?? undefined}
    >
      <div
        aria-hidden="true"
        ref={(element) => element?.setAttribute("inert", "")}
        className={cn(
          "pointer-events-none col-start-1 row-start-1 min-w-0 select-none blur-[2.5px] saturate-50",
          variant !== "compact" && "opacity-70",
          contentClassName,
        )}
      >
        {children}
      </div>

      <div
        className={cn(
          "relative z-10 col-start-1 row-start-1 flex items-center justify-center bg-background/55 backdrop-blur-[2px]",
          variant === "compact" ? "px-3 py-1" : "p-5 sm:p-8",
        )}
        data-testid={`plan-gate-${feature}`}
        role="region"
        aria-label={upgradeText}
      >
        <div
          className={cn(
            "flex items-center border border-border/80 bg-card/95 text-center shadow-lg",
            variant === "compact"
              ? "max-w-full gap-2 rounded-lg px-3 py-1.5"
              : "max-w-md flex-col rounded-2xl px-6 py-7",
          )}
        >
          <span
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary",
              variant === "compact" ? "h-7 w-7" : "h-12 w-12",
            )}
          >
            <LockKeyhole className={variant === "compact" ? "h-3.5 w-3.5" : "h-5 w-5"} />
          </span>
          <div className={variant === "compact" ? "min-w-0 text-left" : "mt-4"}>
            <p
              className={cn(
                "font-semibold text-foreground",
                variant === "compact" ? "truncate text-xs" : "text-base sm:text-lg",
              )}
            >
              {upgradeText}
            </p>
            {variant !== "compact" && (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Your current plan stays active. Upgrade when you are ready to use this feature.
              </p>
            )}
          </div>
          {showUpgradeAction && canManageBilling && (
            <Button
              type="button"
              size="sm"
              className={variant === "compact" ? "ml-1 h-7 shrink-0 px-2.5 text-xs" : "mt-5"}
              onClick={openBilling}
            >
              View plans
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanFeatureGate;
